/**
 * For a created Repair Order, all fields are autosave and must persist. To ensure data integrity
 * and continuity in user interactions, we explicitly track and update using `prevState`.
 * This approach handles successive changes, accommodating immediate, incremental
 * autosaves without losing interim user inputs or causing race conditions. All autosave
 * fields utilize this pattern to ensure data consistency.
 */

import { ChevronLeftIcon, ClockIcon, PlusIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import * as _ from 'lodash-es'
import { observer } from 'mobx-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { AutoCompleteCombobox } from '@/components/forms/AutoCompleteCombobox'
import { DatePicker } from '@/components/forms/DatePicker'
import { TextInput } from '@/components/forms/TextInput'
import { Toggle } from '@/components/forms/Toggle'
import { CommentsIcon, PencilIcon } from '@/components/icons'
import { Footer } from '@/components/layout/Footer'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { StateContainer } from '@/components/ui/StateContainer'
import { Tooltip } from '@/components/ui/Tooltip'
import { getDealer } from '@/services/dealer.service'
import { getManufacturerActions } from '@/services/manufacturers.service'
import {
  createRepairOrder,
  getRepairOrder,
  getRepairOrderAdvisors,
  getRepairOrderTechnicians,
  updateRepairOrder,
} from '@/services/repairOrders.service'
import { GlobalSettingsContext } from '@/stores/GlobalSettingsStore'
import { NotificationStoreContext } from '@/stores/NotificationStore'
import { UserStoreContext } from '@/stores/UserStore'
import { setDealerGroup } from '@/utils/analytics'
import { formatDate, formatDateTime } from '@/utils/formatters'
import { toast, verifyCurrencyInputValue } from '@/utils/helpers'

import DeleteRepairOrderModal from './DeleteRepairOrderModal'
import LineItemModal from './LineItemModal'
import MarkInternalModal from './MarkInternalModal'
import PaymentReviewModal from './PaymentReviewModal'
import ReopenRepairOrderModal from './ReopenRepairOrderModal'
import RepairOrderChat from './RepairOrderChat'
import RepairOrderChatSkeleton from './RepairOrderChatSkeleton'
import RepairOrderLineItem from './RepairOrderLineItem'
import RepairOrderModal from './RepairOrderModal'
import { RepairOrderDatesSkeleton, RepairOrderDetailSkeleton } from './RepairOrderSkeleton'
import RepairOrderTimeline from './RepairOrderTimeline'
import ResolveRepairOrderModal from './ResolveRepairOrderModal'
import ReturnToIntakeModal from './ReturnToIntakeModal'

const RepairOrder = observer(() => {
  // Context
  const location = useLocation()
  const navigate = useNavigate()
  const { dealerId, id } = useParams()
  const { isDealerAdmin, isDealerUser, isReadOnlySession, isWpcUser } =
    useContext(UserStoreContext)
  const { unreadNotificationsCount } = useContext(NotificationStoreContext)
  const { isRoTimelineEnabled } = useContext(GlobalSettingsContext)

  // If the user is a WPC user or if they are a Dealer Admin, they are on the admin side of the
  // repair order and have the same permissions. This is to encorporate Dealer Managed workflows.
  const isROAdminUser = isWpcUser || isDealerAdmin
  const isROServiceUser = isDealerUser && !isDealerAdmin

  // State
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingCreateRepairOrder, setLoadingCreateRepairOrder] = useState(false)
  const [loadingReturn, setLoadingReturn] = useState(false)
  const [updatedDealer, setUpdatedDealer] = useState(location.state?.dealer || null)
  const [updatedRepairOrder, setUpdatedRepairOrder] = useState(null)
  const [isRepairOrderModalOpen, setIsRepairOrderModalOpen] = useState(false)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [showDateAddedPicker, setShowDateAddedPicker] = useState(false)
  const [allAdvisors, setAllAdvisors] = useState([])
  const [filteredAdvisors, setFilteredAdvisors] = useState([])
  const [advisorQuery, setAdvisorQuery] = useState('')
  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false)
  const [lineItems, setLineItems] = useState([])
  const [allTechnicians, setAllTechnicians] = useState([])
  const [actionOptions, setActionOptions] = useState([])
  const [defaultTechnician, setDefaultTechnician] = useState('')
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)
  const [isResolveRepairOrderModalVisible, setIsResolveRepairOrderModalVisible] = useState(false)
  const [isMarkInternalModalVisible, setIsMarkInternalModalVisible] = useState(false)
  const [isReopenRepairOrderModalVisible, setIsReopenRepairOrderModalVisible] = useState(false)
  const [isDeleteRepairOrderModalVisible, setIsDeleteRepairOrderModalVisible] = useState(false)
  const [isReturnToIntakeModalVisible, setIsReturnToIntakeModalVisible] = useState(false)
  const [isPaymentReviewModalVisible, setIsPaymentReviewModalVisible] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(() => !!id && window.innerWidth > 1024)
  const [isTimelineOpen, setIsTimelineOpen] = useState(false)
  // Chat and History share the right-hand column; main-content layout reacts to
  // either being open, not just chat.
  const isSidePanelOpen = isChatOpen || isTimelineOpen
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [selectedLineItem, setSelectedLineItem] = useState(null)
  const [transferrableStatuses, setTransferrableStatuses] = useState({})

  // Refs
  const initialLoadRef = useRef(true)

  const handleSuccess = (message) => toast(message, 'success')
  const handleError = (message) => toast(message, 'error')

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit: handleCreateSubmit,
    register,
    watch,
    setError: setFormError,
    setValue,
  } = useForm({
    defaultValues: {
      roNumber: '',
      claimAmount: 0,
      advisor: '',
      agesOut: null,
      isAgesOutOverridden: false,
      timeClockTriggerDate: null,
      isOpen: true,
      isDateAddedOverridden: false,
    },
  })

  const getUpdatedRepairOrder = async (silent = false, redirectOnResolve = false) => {
    let roSetLoading = setLoading
    if (silent) {
      roSetLoading = () => {}
    }
    const repairOrder = await getRepairOrder(dealerId, id, roSetLoading, setError)

    if (repairOrder) {
      setLineItems(repairOrder.lineItems)
      setUpdatedRepairOrder(repairOrder)

      if (repairOrder.lineItems.length > 0) {
        // Recompute completed actions
        const completedActions = _.flatten(
          repairOrder.lineItems.map((lineItem) => {
            const actionNeeded = lineItem.actionsNeeded.filter((action) => action.completedAt)
            return actionNeeded.map((needed) => needed.action?.name)
          }),
        )

        const actions = await getManufacturerActions(repairOrder.manufacturer)
        if (actions) {
          setActionOptions(
            _.map(
              _.filter(actions, (a) => {
                // Return actions that align with the RO open status OR are completed already
                if (repairOrder.isOpen && a.displayForOpenRo) return true
                if (!repairOrder.isOpen && a.displayForClosedRo) return true
                if (completedActions.includes(a.name)) return true
                return false
              }),
              (a) => ({ ...a, label: a.name }),
            ),
          )
        }

        const technicians = await getRepairOrderTechnicians(dealerId)
        if (technicians) setAllTechnicians(technicians)

        setDefaultTechnician(repairOrder.lineItems[0].technician || '')
      }

      if (redirectOnResolve && repairOrder.state === 'Resolved') {
        if (repairOrder.pendingPaymentReview) {
          setIsPaymentReviewModalVisible(true)
        } else {
          handleSuccess('All line items complete. Repair order resolved.')
          navigate(`/dealer/${dealerId}/detail`)
        }
      }
    }
  }

  useEffect(() => {
    setError(null)

    const getUpdatedData = async () => {
      setLoading(true)

      const dealer = await getDealer(dealerId, () => {}, setError)
      if (dealer) {
        setUpdatedDealer(dealer)
        setDealerGroup(dealer)
      }

      const advisors = await getRepairOrderAdvisors(dealerId)
      if (advisors) setAllAdvisors(advisors)

      if (id) await getUpdatedRepairOrder()

      setLoading(false)
    }

    getUpdatedData()
  }, [dealerId, id])

  useEffect(() => {
    if (advisorQuery.length >= 2) {
      const lowerCaseQuery = advisorQuery.toLowerCase()

      setFilteredAdvisors(
        allAdvisors.filter((person) => person.toLowerCase().includes(lowerCaseQuery)),
      )
    } else {
      setFilteredAdvisors([])
    }
  }, [advisorQuery, allAdvisors])

  useEffect(() => {
    // Skip the first render since the other useEffect will handle it
    if (initialLoadRef.current) {
      initialLoadRef.current = false
      return
    }

    // Only get updated repair order data if we are viewing a created one
    if (id) getUpdatedRepairOrder(true)
  }, [unreadNotificationsCount])

  // Set the screen size for the mobile view
  const handleResize = () => {
    if (window.innerWidth <= 1200) {
      setIsMobile(true)
      setIsChatOpen(false)
    } else {
      setIsMobile(false)
    }
  }

  // Create an event listener for resizing the window
  useEffect(() => {
    const listener = window.addEventListener('resize', handleResize)

    return () => {
      if (listener) window.removeEventListener('resize', listener)
    }
  }, [])

  useEffect(() => {
    if (isSidePanelOpen) {
      // Prevent scrolling on the body when a side panel (chat/history) is open
      document.body.style.overflow = 'hidden'
    } else {
      // Allow scrolling again when both are closed
      document.body.style.overflow = ''
    }

    // Cleanup function to restore scrolling when the component unmounts
    return () => {
      document.body.style.overflow = ''
    }
  }, [isSidePanelOpen])

  const adminStatusPrefix = useMemo(() => {
    if (!updatedDealer) return ''
    return isDealerUser && updatedDealer?.isDealerManaged ? 'Admin' : 'WPC'
  }, [updatedDealer, isDealerAdmin])
  const repairOrderState = updatedRepairOrder?.state

  const configureReviewTagDetails = () => {
    // Configuration mapping states and user types to their details. The labels name whose
    // review it is, because "Requires Review" alone let dealers stall ROs in WPC's list
    // (2026-10-01). Gold is always WPC Review and blue always Dealer Review, as on the
    // Outstanding List's tabs and rows.
    const tagConfig = {
      Draft: {
        dealer: { text: '', styling: '' },
        wpc: { text: 'Draft', styling: 'text-charcoal-400' },
      },
      'Dealer Review': {
        dealer: { text: 'Requires Dealer Review', styling: 'text-blue-900' },
        wpc: { text: 'In Dealer Review', styling: 'text-blue-900' },
      },
      'WPC Review': {
        dealer: { text: `In ${adminStatusPrefix} Review`, styling: 'text-gold-dark' },
        wpc: { text: 'Requires Admin Review', styling: 'text-gold-dark' },
      },
    }

    // Determine user type key
    const userType = isROServiceUser ? 'dealer' : 'wpc'

    // Return the corresponding configuration or a default
    return tagConfig[repairOrderState]?.[userType] || { text: '', styling: '' }
  }

  const { text: reviewTagText, styling: reviewTagStyling } = configureReviewTagDetails()

  const renderReviewTag = (text, classNames) => {
    if (!text) return null

    return (
      <div className={`flex items-center rounded-full bg-[#ffffff] px-2 py-1 ${classNames}`}>
        <span className="text-xs font-semibold uppercase leading-tight">{text}</span>
      </div>
    )
  }

  // Check if the RO is transferrable
  const canTransferRepairOrder = useMemo(
    () => Object.values(transferrableStatuses).every((status) => status === true),
    [transferrableStatuses],
  )

  const handleTransferrableChange = useCallback((lineItemId, isTransferrable) => {
    setTransferrableStatuses((prevStatuses) => ({
      ...prevStatuses,
      [lineItemId]: isTransferrable,
    }))
  }, [])

  // Check if every line item is resolved or written off
  const areAllLineItemsResolvedOrWrittenOff = useMemo(
    () =>
      lineItems?.every(
        (item) => item.state === 'Resolved' || item.state === 'Resolved - Written Off',
      ),
    [lineItems],
  )

  const isRepairOrderDraft = repairOrderState === 'Draft'

  // Determine if the "Send to Dealer" button should be visible (DRAFT only)
  const isSendToDealerVisible = isROAdminUser && isRepairOrderDraft

  // An RO can't be sent/returned to the dealer until its required fields are complete — an
  // Advisor #, at least one line item, and (via canTransferRepairOrder) an action item +
  // problem on every line item. RepairOrderFlow.{send_to_dealer,pass_to_dealer} enforces the
  // same server-side; these guards give inline feedback instead of a failed request. Note the
  // client is the stricter of the two: canTransferRepairOrder also requires a technician per
  // line item, which the backend guard does not check.
  const hasAdvisor = Boolean(String(updatedRepairOrder?.advisor ?? '').trim())
  const hasLineItems = lineItems.length > 0
  const isReadyForDealer = hasAdvisor && canTransferRepairOrder && hasLineItems

  /**
   * Explains why a transition button is disabled, phrased for the direction it blocks.
   * `requiresAdvisor` is true only for the directions that hand the RO to the dealer —
   * returning it to WPC/Admin has the same line-item requirements but needs no Advisor #.
   */
  const notReadyMessage = ({ destination, requiresAdvisor }) => {
    if (requiresAdvisor && !hasAdvisor) return `Add an Advisor # before ${destination}.`
    // `canTransferRepairOrder` is vacuously true with no line items, so this case has to be
    // checked before the per-line-item message or the tooltip would ask the user to complete
    // line items that don't exist yet.
    if (!hasLineItems) return `Add at least one line item before ${destination}.`
    return `Complete every line item (technician, action item, and problem) before ${destination}.`
  }

  const notReadyForDealerMessage = notReadyMessage({
    destination: 'sending to the dealer',
    requiresAdvisor: true,
  })

  // Determine if the "Resolve RO" button should be visible
  const isResolveROVisible =
    isROAdminUser && areAllLineItemsResolvedOrWrittenOff && !isRepairOrderDraft

  // WARP-957: Internal is WPC's call, for an RO Intake dealer's RO that is still open in
  // the DMS. Anything ever closed (dealer closed, or submitted before) can only be
  // written off. The API checks the same, plus the RO's status history.
  const isMarkInternalVisible =
    isWpcUser &&
    Boolean(updatedDealer?.isRoIntakeEnabled) &&
    updatedRepairOrder?.status === 'On Outstanding List' &&
    Boolean(updatedRepairOrder?.isOpen) &&
    !updatedRepairOrder?.dealerClosed &&
    !updatedRepairOrder?.previouslySubmitted

  // Determine if the "Return" button should be visible
  const isReturnButtonVisible =
    !isRepairOrderDraft &&
    ((repairOrderState === 'Dealer Review' && isROServiceUser) ||
      (!areAllLineItemsResolvedOrWrittenOff &&
        (isROAdminUser || repairOrderState !== 'WPC Review')))

  /**
   * Only render reopened date if the `createdAt` and `openedAt` dates are different.
   */
  const renderReopenedDate = () => {
    const createdAt = dayjs(updatedRepairOrder?.createdAt)
    const openedAt = dayjs(updatedRepairOrder?.openedAt)

    // Check if dates are the same, ignoring seconds and milliseconds
    if (createdAt.isValid() && openedAt.isValid() && createdAt.isSame(openedAt, 'minute')) {
      return null
    }

    return <span>Date Reopened: {dayjs(updatedRepairOrder.openedAt).format('MM/DD/YYYY')}</span>
  }

  const renderRepairOrderDateField = () => {
    const isOpen = watch('isOpen')
    const isAgesOutOverridden = watch('isAgesOutOverridden')
    const trigger = updatedDealer?.manufacturer.timeClockTrigger

    if (isOpen && !isAgesOutOverridden && trigger === 'RO close date') {
      return (
        <span className="mb-6 text-center text-xs font-light italic leading-5 tracking-[1.25px]">
          For this manufacturer, all open Repair Orders will age out 30 days after the date added
          to the Outstanding List.{' '}
        </span>
      )
    }

    const name = isAgesOutOverridden ? 'agesOut' : 'timeClockTriggerDate'

    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => {
          let errorMessage = ''
          if (isAgesOutOverridden && errors.agesOut) {
            errorMessage = errors.agesOut.message || 'This field is required'
          } else if (!isAgesOutOverridden && errors.timeClockTriggerDate) {
            errorMessage = errors.timeClockTriggerDate.message || 'This field is required'
          }

          return (
            <DatePicker
              disabled={loading}
              dataTestId="timeClockTriggerDate"
              error={errorMessage ? { message: errorMessage } : null}
              label={
                isAgesOutOverridden
                  ? 'Age Out Date Override (MM/DD/YYYY)'
                  : `${trigger} (MM/DD/YYYY)`
              }
              name={name}
              onChange={onChange}
              show={showDatePicker}
              setShow={setShowDatePicker}
              value={value}
            />
          )
        }}
        rules={{
          required: true,
        }}
      />
    )
  }

  /**
   * Determines the appropriate label for the date field.  If the Age Out Date has
   * been overridden, it returns 'Age Out Date Override'. If the manufacturer's time
   * clock trigger is 'RO close date' and the RO is still open, it returns
   * 'Age Out Date'. Otherwise, it returns the time clock trigger label.
   *
   * @returns {string} - The appropriate label for the date field.
   */
  const getDateFieldLabel = () => {
    if (updatedRepairOrder?.isAgesOutOverridden) {
      return 'Age Out Date Override'
    }
    if (
      updatedDealer?.manufacturer.timeClockTrigger === 'RO close date' &&
      updatedRepairOrder?.isOpen
    ) {
      return 'Age Out Date'
    }
    return updatedDealer?.manufacturer.timeClockTrigger
  }

  /**
   * Determines the appropriate value for the date field.
   * If the Age Out Date has been overridden, it returns
   * the overridden Age Out Date. If the manufacturer's time
   * clock trigger is 'RO close date' and the RO is still open, it returns
   * the Age Out Date.  Otherwise, it returns the time clock trigger date.
   *
   * @returns {string|Date} - The appropriate date value for the field.
   */
  const getDateFieldValue = () => {
    if (
      updatedRepairOrder?.isAgesOutOverridden ||
      (updatedDealer?.manufacturer.timeClockTrigger === 'RO close date' &&
        updatedRepairOrder?.isOpen)
    ) {
      return updatedRepairOrder?.agesOut
    }
    return updatedRepairOrder?.timeClockTriggerDate
  }

  // Chat and History share the right-hand column and are mutually exclusive.
  const isMainContentVisible = !(isMobile && isSidePanelOpen)

  const toggleChat = () => {
    setIsChatOpen((prev) => {
      if (!prev) setIsTimelineOpen(false)
      return !prev
    })
  }

  const toggleTimeline = () => {
    setIsTimelineOpen((prev) => {
      if (!prev) setIsChatOpen(false)
      return !prev
    })
  }

  const isDealerReview = repairOrderState === 'Dealer Review'
  const isWpcReview = repairOrderState === 'WPC Review'
  const isRepairOrderResolved = repairOrderState === 'Resolved'

  // The Return button hands the RO to the dealer from WPC Review and back to WPC/Admin
  // otherwise. Both directions need complete line items; only the dealer one needs an Advisor #.
  const isReturnReady = hasLineItems && canTransferRepairOrder && (!isWpcReview || hasAdvisor)
  const notReadyToReturnMessage = notReadyMessage({
    destination: isWpcReview ? 'returning to the dealer' : `returning to ${adminStatusPrefix}`,
    requiresAdvisor: isWpcReview,
  })

  // WPC users can't add line items while the RO is in Dealer Review — it's the dealer's turn.
  const isAddLineItemBlockedForWpc = isWpcUser && isDealerReview

  const pageTitle = useMemo(() => {
    if (updatedRepairOrder?.roNumber && updatedDealer?.name) {
      return `RO #${updatedRepairOrder.roNumber} - ${updatedDealer.name}`
    }
    if (updatedDealer?.name) {
      return `New RO - ${updatedDealer.name}`
    }
    return 'Repair Order'
  }, [updatedRepairOrder?.roNumber, updatedDealer?.name])

  return (
    <div
      className={mergeClassNames(
        'flex h-full w-full',
        isMainContentVisible ? 'flex-row' : 'flex-col',
      )}
    >
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <div
        className={mergeClassNames(
          'bg-background flex w-full flex-row overflow-y-auto',
          isMainContentVisible ? 'h-full' : 'h-auto',
        )}
      >
        <StateContainer error={error} loading={false}>
          <div
            className={mergeClassNames(
              'flex flex-col px-2 py-6 transition-all duration-300 ease-in-out sm:px-8 lg:px-10',
              isMainContentVisible && 'min-h-[calc(100vh-183px)]',
            )}
          >
            {/* CHAT AND NAVIGATION */}
            <div className="mb-4 flex flex-col items-center justify-between gap-4 sm:flex-row">
              <button
                className="flex flex-row items-center gap-2"
                onClick={() => navigate(`/dealer/${dealerId}/detail`)}
                type="button"
              >
                <ChevronLeftIcon className="h-5" />
                <span className="text-charcoal-900 text-xs font-bold uppercase leading-5 tracking-[3px] sm:text-sm">
                  Back to Outstanding List
                </span>
              </button>

              <div className="flex items-center gap-4">
                {id && (
                  <Button
                    icon={
                      <CommentsIcon
                        className="h-6 fill-white"
                        unread={updatedRepairOrder?.hasUnreadComments}
                      />
                    }
                    label={isChatOpen ? 'Hide Chat' : 'Show Chat'}
                    onClick={toggleChat}
                    size="sm"
                    dataTestId="chatToggle"
                  />
                )}

                {id && isRoTimelineEnabled && (
                  <Button
                    icon={<ClockIcon className="h-6 stroke-white" />}
                    label={isTimelineOpen ? 'Hide History' : 'Show History'}
                    onClick={toggleTimeline}
                    size="sm"
                    dataTestId="timelineToggle"
                  />
                )}
              </div>
            </div>

            {isMainContentVisible && (
              <>
                {/* MAIN CONTENT */}
                <div className="mb-4 flex w-full flex-col justify-between gap-2 sm:flex-row">
                  {updatedDealer && (
                    <div className="flex flex-col gap-3">
                      <span className="text-charcoal-900 text-lg font-bold">
                        {updatedDealer?.name}
                      </span>

                      <div className="text-charcoal-900 flex flex-col gap-0.5 text-sm font-medium leading-5">
                        <span>WPC Dealer ID: {updatedDealer.code?.replace('.0', '')}</span>
                        {updatedDealer.group && (
                          <span>Group Name: {updatedDealer.group?.name}</span>
                        )}
                        {updatedDealer.manufacturer && (
                          <span>Manufacturer: {updatedDealer.manufacturer?.name}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {updatedRepairOrder ? (
                    <div className="animate-fade-in flex flex-col gap-3 sm:text-end">
                      <div className="flex flex-col">
                        <span className="text-charcoal-900 text-lg font-bold">
                          Ages Out: {dayjs(updatedRepairOrder.agesOut).format('MM/DD/YYYY')}
                        </span>
                        <span className="text-charcoal-800 text-xs italic">
                          3 business days prior to Manufacturer guideline
                        </span>
                      </div>

                      <div className="text-charcoal-900 flex flex-col gap-0.5 text-sm font-medium leading-5">
                        <span>
                          Date Added to Outstanding List:{' '}
                          {dayjs(updatedRepairOrder.createdAt).format('MM/DD/YYYY')}
                        </span>

                        {renderReopenedDate()}

                        <span>Days on Outstanding List: {updatedRepairOrder.daysOnList}</span>

                        {isRepairOrderResolved ? (
                          <span>Resolved On: {formatDate(updatedRepairOrder.resolvedAt)}</span>
                        ) : (
                          <span>
                            Last Updated: {formatDateTime(updatedRepairOrder.lastUpdatedOn)}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    id && <RepairOrderDatesSkeleton />
                  )}
                </div>

                {/* eslint-disable-next-line no-nested-ternary */}
                {updatedRepairOrder ? (
                  <div className="animate-fade-in">
                    <div className="bg-brownGray-100 flex w-full flex-col gap-4 rounded-2xl px-6 py-4">
                      <div className="flex flex-row items-center justify-between">
                        <div className="flex items-center justify-center">
                          <span className="text-charcoal-900 text-sm font-bold leading-5">
                            General
                          </span>

                          {isROAdminUser && !isRepairOrderResolved && (
                            <button
                              aria-label={`Edit Repair Order: ${updatedRepairOrder.roNumber}`}
                              onClick={() => {
                                setIsRepairOrderModalOpen(true)
                              }}
                              className="ml-1 inline-flex items-center rounded-lg p-2 text-center hover:bg-gray-100 disabled:cursor-not-allowed"
                              type="button"
                              disabled={isReadOnlySession}
                              data-testid="editRepairOrder"
                            >
                              <PencilIcon className="h-4 stroke-white" />
                            </button>
                          )}
                        </div>

                        <div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
                          {renderReviewTag(reviewTagText, reviewTagStyling)}
                          <div
                            className={`rounded-full px-3 py-1 ${
                              updatedRepairOrder.isOpen ? 'bg-blue-light' : 'bg-gray-300'
                            } flex w-full flex-col text-center sm:w-auto sm:flex-row sm:items-center sm:justify-center`}
                          >
                            <span
                              className={`text-xs font-semibold leading-tight ${
                                updatedRepairOrder.isOpen ? 'text-blue-950' : 'text-gray-700'
                              }`}
                            >
                              {updatedRepairOrder.isOpen ? 'OPEN' : 'CLOSED'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div
                        className={`flex h-full w-full flex-col justify-between gap-4 md:flex-row ${
                          isSidePanelOpen ? 'flex-wrap' : ''
                        }`}
                      >
                        <TextInput
                          data-testid="roNumber"
                          disabled
                          label="REPAIR ORDER #"
                          inputStyles="uppercase"
                          id="roNumber"
                          fullWidth
                          value={updatedRepairOrder.roNumber}
                        />

                        <TextInput
                          data-testid="claimAmount"
                          disabled
                          label="Claim Amount"
                          id="claimAmount"
                          infoTooltip={
                            updatedRepairOrder.isOpen
                              ? 'Claim amount is the total of all Line Items and any additional dollar amount held up.'
                              : 'Claim amount is the total of all unresolved Line Items.'
                          }
                          fullWidth
                          value={`${Number(
                            updatedRepairOrder.totalUnresolvedClaimAmount,
                          ).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })}`}
                        />

                        <TextInput
                          data-testid="advisor"
                          disabled
                          label="ADV #"
                          id="advisor"
                          fullWidth
                          value={updatedRepairOrder.advisor}
                        />

                        <TextInput
                          data-testid="timeClockTriggerDate"
                          disabled
                          label={getDateFieldLabel()}
                          id="timeClockTriggerDate"
                          fullWidth
                          value={dayjs(getDateFieldValue()).format('MM/DD/YYYY')}
                        />
                      </div>
                    </div>

                    <div className="w-full pt-8">
                      {lineItems.map((lineItem) => (
                        <RepairOrderLineItem
                          actionOptions={actionOptions}
                          dealerId={dealerId}
                          defaultOpen={lineItem.state === 'Pending'}
                          getUpdatedRepairOrder={getUpdatedRepairOrder}
                          isDealerReview={isDealerReview}
                          isRepairOrderResolved={isRepairOrderResolved}
                          key={lineItem.id}
                          lineItem={lineItem}
                          openEditModal={(item) => {
                            setSelectedLineItem(item)
                            setIsLineItemModalOpen(true)
                          }}
                          onTransferrableChange={(isTransferrable) =>
                            handleTransferrableChange(lineItem.id, isTransferrable)
                          }
                          repairOrderId={updatedRepairOrder.id}
                          roIsOpen={updatedRepairOrder.isOpen}
                        />
                      ))}
                    </div>

                    {!isRepairOrderResolved && (
                      <>
                        {isROAdminUser && (
                          <Tooltip
                            className={mergeClassNames(
                              'mb-8 w-fit',
                              isAddLineItemBlockedForWpc && 'cursor-not-allowed',
                            )}
                            content={
                              <div className="bg-charcoal-950 max-w-[260px] rounded-md p-2 text-center">
                                <p className="text-sm text-white">
                                  Line items can&apos;t be added while the repair order is in
                                  Dealer Review.
                                </p>
                              </div>
                            }
                            display={isAddLineItemBlockedForWpc}
                            placement="top"
                          >
                            <Button
                              className={
                                isAddLineItemBlockedForWpc ? 'disabled:pointer-events-none' : ''
                              }
                              icon={<PlusIcon className="h-5 stroke-white" />}
                              label="Add Line Item"
                              onClick={() => setIsLineItemModalOpen(true)}
                              size="sm"
                              disabled={isReadOnlySession || isAddLineItemBlockedForWpc}
                              dataTestId="addLineItem"
                            />
                          </Tooltip>
                        )}
                        {/* The RO-level actions, side by side: the next step first, then
                            Mark as Internal. Wraps on a narrow screen. */}
                        <div className="flex flex-wrap items-center justify-center gap-3">
                          {/* Show Return to Dealer / Return to WPC button if conditions are met */}
                          {isReturnButtonVisible && (
                            <Tooltip
                              className="w-fit"
                              content={
                                <div className="bg-charcoal-950 max-w-[260px] rounded-md p-2 text-center">
                                  <p className="text-sm text-white">{notReadyToReturnMessage}</p>
                                </div>
                              }
                              display={!isReturnReady && !isReadOnlySession}
                              placement="top"
                            >
                              <Button
                                dataTestId="returnRepairOrder"
                                label={
                                  isWpcReview
                                    ? 'Return to Dealer'
                                    : `Return to ${adminStatusPrefix}`
                                }
                                onClick={() => setIsConfirmationModalVisible(true)}
                                size="sm"
                                type="button"
                                disabled={!isReturnReady || isReadOnlySession}
                              />
                            </Tooltip>
                          )}
                          {/* Show Send to Dealer button for DRAFT ROs */}
                          {isSendToDealerVisible && (
                            <Tooltip
                              className="w-fit"
                              content={
                                <div className="bg-charcoal-950 max-w-[260px] rounded-md p-2 text-center">
                                  <p className="text-sm text-white">{notReadyForDealerMessage}</p>
                                </div>
                              }
                              display={!isReadyForDealer && !isReadOnlySession && !loadingReturn}
                              placement="top"
                            >
                              <Button
                                label="Send to Dealer"
                                onClick={async () => {
                                  await updateRepairOrder(
                                    dealerId,
                                    id,
                                    { markAsSentToDealer: true },
                                    setLoadingReturn,
                                    handleError,
                                    () => {
                                      handleSuccess('Repair order sent to dealer.')
                                      // Sending a Draft on pushes it to the dealer's queue —
                                      // go back to the Outstanding List.
                                      navigate(`/dealer/${dealerId}/detail`)
                                    },
                                  )
                                }}
                                size="sm"
                                type="button"
                                disabled={!isReadyForDealer || isReadOnlySession || loadingReturn}
                              />
                            </Tooltip>
                          )}
                          {/* Show Resolve RO button if conditions are met */}
                          {isResolveROVisible && (
                            <Button
                              dataTestId="resolveRepairOrder"
                              label="Resolve RO"
                              onClick={() => setIsResolveRepairOrderModalVisible(true)}
                              size="sm"
                              type="button"
                              disabled={isReadOnlySession}
                            />
                          )}
                          {isMarkInternalVisible && !isRepairOrderResolved && (
                            <Button
                              // Outlined, so the next step beside it stays the primary action.
                              background="bg-background"
                              label="Mark as Internal"
                              onClick={() => setIsMarkInternalModalVisible(true)}
                              size="sm"
                              type="button"
                              disabled={isReadOnlySession}
                            />
                          )}
                        </div>
                      </>
                    )}

                    {/* Show Reopen RO button if RO is Resolved if user is on the Admin side */}
                    {isROAdminUser && isRepairOrderResolved && (
                      <Button
                        className="mx-auto"
                        label="Reopen RO"
                        onClick={() => setIsReopenRepairOrderModalVisible(true)}
                        size="sm"
                        type="button"
                        disabled={isReadOnlySession}
                      />
                    )}

                    <MarkInternalModal
                      dealerId={dealerId}
                      isOpen={isMarkInternalModalVisible}
                      repairOrderId={id}
                      setIsOpen={setIsMarkInternalModalVisible}
                    />
                    <ResolveRepairOrderModal
                      repairOrder={updatedRepairOrder}
                      isOpen={isResolveRepairOrderModalVisible}
                      setIsOpen={setIsResolveRepairOrderModalVisible}
                      dealerId={dealerId}
                      repairOrderId={id}
                      onPendingPaymentReview={() => setIsPaymentReviewModalVisible(true)}
                    />
                    <PaymentReviewModal
                      isOpen={isPaymentReviewModalVisible}
                      setIsOpen={setIsPaymentReviewModalVisible}
                      dealerId={dealerId}
                      repairOrderId={id}
                    />
                    <ReopenRepairOrderModal
                      isOpen={isReopenRepairOrderModalVisible}
                      setIsOpen={setIsReopenRepairOrderModalVisible}
                      repairOrder={updatedRepairOrder}
                      lineItems={lineItems}
                      repairOrderId={id}
                      dealerId={dealerId}
                      getUpdatedRepairOrder={getUpdatedRepairOrder}
                      defaultTechnician={defaultTechnician}
                    />
                    <DeleteRepairOrderModal
                      isOpen={isDeleteRepairOrderModalVisible}
                      setIsOpen={setIsDeleteRepairOrderModalVisible}
                      repairOrderId={id}
                      dealerId={dealerId}
                    />
                    <ReturnToIntakeModal
                      isOpen={isReturnToIntakeModalVisible}
                      setIsOpen={setIsReturnToIntakeModalVisible}
                      repairOrderId={id}
                      dealerId={dealerId}
                      onSuccess={() => navigate(`/ro-intake/${dealerId}`)}
                    />

                    <LineItemModal
                      allTechnicians={allTechnicians}
                      dealerId={dealerId}
                      defaultTechnician={defaultTechnician}
                      isOpen={isLineItemModalOpen}
                      setIsOpen={setIsLineItemModalOpen}
                      repairOrderId={id}
                      getUpdatedRepairOrder={getUpdatedRepairOrder}
                      lineItem={selectedLineItem}
                      setOpen={setIsLineItemModalOpen}
                      closeModal={() => {
                        setIsLineItemModalOpen(false)
                        setSelectedLineItem(null)
                      }}
                    />

                    <RepairOrderModal
                      getUpdatedRepairOrder={getUpdatedRepairOrder}
                      closeModal={() => setIsRepairOrderModalOpen(false)}
                      isOpen={isRepairOrderModalOpen}
                      repairOrder={updatedRepairOrder}
                      allAdvisors={allAdvisors}
                      setIsOpen={setIsRepairOrderModalOpen}
                      dealer={updatedDealer}
                      setIsDeleteModalOpen={setIsDeleteRepairOrderModalVisible}
                      setIsReturnToIntakeModalOpen={setIsReturnToIntakeModalVisible}
                    />

                    <Modal
                      open={isConfirmationModalVisible}
                      title="Are you sure?"
                      setOpen={setIsConfirmationModalVisible}
                      content={
                        <div className="my-8 flex h-full items-center justify-center">
                          <p className="text-center">
                            {`Please confirm that you'd like to return the repair order to ${
                              isWpcReview ? 'Dealer' : adminStatusPrefix
                            }.`}
                          </p>
                        </div>
                      }
                      actions={[
                        {
                          type: 'submit',
                          label: 'Confirm',
                          onClick: async () => {
                            // Toggle the state based on the current state
                            const payload = isWpcReview
                              ? { markAsDealerReview: true }
                              : { markAsWpcReview: true }

                            await updateRepairOrder(
                              dealerId,
                              id,
                              payload,
                              setLoadingReturn,
                              handleError,
                              () => {
                                handleSuccess(
                                  `Repair order returned to ${
                                    isWpcReview ? 'Dealer' : adminStatusPrefix
                                  }.`,
                                )
                                setIsConfirmationModalVisible(false)
                                getUpdatedRepairOrder()
                                // Redirect to the OL when pushing the RO away from your
                                // own queue (WPC user/admin → Dealer, or Service → WPC).
                                // When an admin pulls a Dealer-Review RO back to their own
                                // queue, stay put so they can keep working it.
                                if (isWpcReview || (isDealerReview && isROServiceUser)) {
                                  navigate(`/dealer/${dealerId}/detail`)
                                }
                              },
                            )
                          },
                        },
                        { type: 'cancel', label: 'Cancel' },
                      ]}
                      loading={loadingReturn}
                    />

                    <div className="h-[20px] flex-none" />
                  </div>
                ) : id ? (
                  <RepairOrderDetailSkeleton />
                ) : (
                  <div className="bg-brownGray-100 flex flex-col gap-2 rounded-2xl px-8 pt-8 md:flex-row md:gap-16">
                    <div className="flex w-full flex-col gap-4">
                      <div className="flex w-full flex-col gap-4">
                        <span className="text-charcoal-900 text-sm font-bold leading-5">
                          General
                        </span>

                        <div className="flex h-full w-full flex-col space-y-5">
                          <TextInput
                            data-testid="roNumber"
                            disabled={loadingCreateRepairOrder}
                            error={errors.roNumber && 'This field is required'}
                            label="REPAIR ORDER #"
                            inputStyles="uppercase"
                            id="roNumber"
                            name="roNumber"
                            placeholder=""
                            required
                            fullWidth
                            autoFocus
                            {...register('roNumber', { required: true })}
                          />

                          <AutoCompleteCombobox
                            id="advisor-id"
                            dataTestId="advisor"
                            disabled={loadingCreateRepairOrder}
                            label="ADV #"
                            value={watch('advisor')}
                            inputClassName="font-medium text-black"
                            onComboboxChange={(value) => {
                              setValue('advisor', value)
                              clearErrors('advisor')
                            }}
                            options={filteredAdvisors}
                            error={errors.advisor?.message}
                            onComboboxInputChange={(event) => {
                              const inputValue = event.target.value
                              setAdvisorQuery(inputValue)
                              setValue('advisor', inputValue, { shouldValidate: true })
                              clearErrors('advisor')
                            }}
                          />

                          {watch('isOpen') && (
                            <Controller
                              name="additionalDollarsHeldUp"
                              control={control}
                              defaultValue=""
                              disabled={loadingCreateRepairOrder}
                              render={({ field: { value, onChange, ...field } }) => (
                                <TextInput
                                  data-testid="additionalDollarsHeldUp"
                                  error={
                                    errors.additionalDollarsHeldUp &&
                                    errors.additionalDollarsHeldUp.message
                                  }
                                  label="Additional Dollars Held Up"
                                  id="additionalDollarsHeldUp"
                                  placeholder=""
                                  fullWidth
                                  currency
                                  prefix="$"
                                  className="text-base"
                                  {...field}
                                  value={value}
                                  onChange={(e) => {
                                    const updatedValue = verifyCurrencyInputValue(
                                      e,
                                      setError,
                                      clearErrors,
                                    )

                                    // Update the form state with the raw value without moving the cursor
                                    if (updatedValue) onChange(updatedValue)
                                  }}
                                />
                              )}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex w-full flex-col gap-4">
                      <span className="text-charcoal-900 text-sm font-bold leading-5">
                        Age Out Date
                      </span>

                      <div className="flex h-full flex-col justify-between gap-4">
                        <div className="flex flex-col gap-4">
                          <Controller
                            name="isOpen"
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                              <Toggle
                                disabled={loadingCreateRepairOrder}
                                label="Open RO?"
                                name="isOpen"
                                onChange={onChange}
                                checked={value}
                                ref={ref}
                              />
                            )}
                          />

                          <Controller
                            name="isAgesOutOverridden"
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                              <Toggle
                                disabled={loadingCreateRepairOrder}
                                label="Requires Age Out Override"
                                name="isAgesOutOverridden"
                                onChange={onChange}
                                checked={value}
                                ref={ref}
                              />
                            )}
                          />

                          <Controller
                            name="isDateAddedOverridden"
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                              <Toggle
                                disabled={loadingCreateRepairOrder}
                                label="Requires Date Added Override"
                                name="isDateAddedOverridden"
                                onChange={onChange}
                                checked={value}
                                ref={ref}
                              />
                            )}
                          />
                        </div>

                        {renderRepairOrderDateField()}

                        {watch('isDateAddedOverridden') && (
                          <Controller
                            name="createdAt"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <DatePicker
                                disabled={loadingCreateRepairOrder}
                                dataTestId="createdAt"
                                error={errors.createdAt}
                                label="Date Added Override (MM/DD/YYYY)"
                                name="createdAt"
                                onChange={onChange}
                                show={showDateAddedPicker}
                                setShow={setShowDateAddedPicker}
                                value={value}
                              />
                            )}
                            rules={{
                              required: 'This field is required',
                            }}
                          />
                        )}
                      </div>

                      <div className="mt-4 flex flex-row justify-end gap-4 pb-8">
                        <Button
                          background="bg-background"
                          disabled={loadingCreateRepairOrder || isReadOnlySession}
                          label="Cancel"
                          onClick={() => navigate(`/dealer/${dealerId}/detail`)}
                          size="sm"
                        />

                        <Button
                          label="Create RO"
                          dataTestId="create-ro"
                          loading={loadingCreateRepairOrder}
                          onClick={handleCreateSubmit(async (data) => {
                            // Verify advisor
                            if (!data.advisor) {
                              setFormError('advisor', {
                                type: 'manual',
                                message: 'Please select an advisor.',
                              })
                              return
                            }

                            const payload = {
                              ...data,
                              roNumber: data.roNumber.toUpperCase(),
                            }

                            if (data.isAgesOutOverridden && data.agesOut) {
                              // Verify agesOut is a valid date
                              if (!dayjs(data.agesOut).isValid()) {
                                setFormError('agesOut', {
                                  type: 'manual',
                                  message: 'Please enter a valid date (MM/DD/YYYY).',
                                })
                                return
                              }

                              payload.agesOut = dayjs(data.agesOut).format('YYYY-MM-DD')

                              // Clear out `timeClockTriggerDate`, we only need one date
                              payload.timeClockTriggerDate = null
                            } else if (data.timeClockTriggerDate) {
                              // Verify timeClockTriggerDate is a valid date
                              if (!dayjs(data.timeClockTriggerDate).isValid()) {
                                setFormError('timeClockTriggerDate', {
                                  type: 'manual',
                                  message: 'Please enter a valid date (MM/DD/YYYY).',
                                })
                                return
                              }

                              // The time clock trigger is a past real-world event (e.g. RO close
                              // date); it can't be in the future.
                              if (dayjs(data.timeClockTriggerDate).isAfter(dayjs(), 'day')) {
                                setFormError('timeClockTriggerDate', {
                                  type: 'manual',
                                  message: 'The date can’t be in the future.',
                                })
                                return
                              }

                              payload.timeClockTriggerDate = dayjs(
                                data.timeClockTriggerDate,
                              ).format('YYYY-MM-DD')

                              // Clear out `agesOut`, we only need one date
                              payload.agesOut = null
                            }

                            if (data.createdAt) {
                              // Verify createdAt is a valid date
                              if (!dayjs(data.createdAt).isValid()) {
                                setFormError('createdAt', {
                                  type: 'manual',
                                  message: 'Please enter a valid date (MM/DD/YYYY).',
                                })
                                return
                              }

                              payload.createdAt = dayjs(data.createdAt).format('YYYY-MM-DD')
                            }

                            const response = await createRepairOrder(
                              dealerId,
                              payload,
                              setLoadingCreateRepairOrder,
                              (err) => {
                                if (err.roId) {
                                  handleError({
                                    ...err,
                                    linkLabel: `RO: ${payload.roNumber}`,
                                    url: `/dealer/${dealerId}/repair-order/${err.roId}`,
                                  })
                                } else {
                                  handleError(err)
                                }
                              },
                              handleSuccess,
                            )

                            if (response) {
                              navigate(`/dealer/${dealerId}/repair-order/${response.id}`)
                            }
                          })}
                          size="sm"
                          disabled={isReadOnlySession}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Hide the Footer if the Chat slide-out is open on mobile */}
          {isMainContentVisible && <Footer />}
        </StateContainer>
      </div>

      {isChatOpen && updatedRepairOrder?.id && (
        <div className="h-full">
          <RepairOrderChat
            dealerId={dealerId}
            repairOrderId={updatedRepairOrder.id}
            isRepairOrderResolved={isRepairOrderResolved}
            onCommentsRead={() => {
              setUpdatedRepairOrder((prev) => ({
                ...prev,
                hasUnreadComments: false,
              }))
            }}
          />
        </div>
      )}

      {loading && id && isChatOpen && !updatedRepairOrder?.id && window.innerWidth > 1024 && (
        <RepairOrderChatSkeleton />
      )}

      {isTimelineOpen && updatedRepairOrder?.id && (
        <div className="h-full">
          <RepairOrderTimeline
            repairOrderId={updatedRepairOrder.id}
            onClose={() => setIsTimelineOpen(false)}
          />
        </div>
      )}
    </div>
  )
})

export default RepairOrder
